import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { resolvePublicSlug } from "./scheduleBookingService";
import BookingCard from "./BookingCard";

/**
 * PublicBookingPage — member-facing booking page, mounted at `/book/:slug`.
 * Resolves the slug → orgId, then renders BookingCard in "live" mode.
 *
 * Route:  <Route path="/book/:slug" element={<PublicBookingPage />} />
 */

const BG = "#f9fafb";
const FONT = "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const INK = "#111827";
const MUTED = "#6B7280";
const BORDER = "#E5E7EB";

function Shell({ children }) {
  return (
    <main style={{ minHeight: "100vh", background: BG, fontFamily: FONT, display: "flex", flexDirection: "column", alignItems: "center", padding: "28px 16px 48px" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
      <div style={{ width: "100%", maxWidth: 520, display: "flex", flexDirection: "column", gap: 16 }}>{children}</div>
    </main>
  );
}

function Card({ children }) {
  return <div style={{ background: "#fff", border: "1px solid rgba(229,231,235,0.8)", borderRadius: 24, boxShadow: "0 1px 2px rgba(0,0,0,0.04)", overflow: "hidden", padding: 20 }}>{children}</div>;
}

export default function PublicBookingPage() {
  const { slug } = useParams();
  const [orgId, setOrgId] = useState(null);
  const [seedServices, setSeedServices] = useState([]);
  const [primaryColor, setPrimaryColor] = useState(undefined);
  const [phase, setPhase] = useState("resolving"); // resolving | ready | notfound

  useEffect(() => {
    let cancelled = false;
    setPhase("resolving");
    (async () => {
      try {
        const res = await resolvePublicSlug(slug);
        if (cancelled) return;
        setOrgId(res?.orgId ?? res?.org?.id ?? null);
        // The resolved services are the authoritative list of published
        // services; BookingCard uses them as a seed so a service always shows.
        setSeedServices(res?.services || []);
        setPrimaryColor(res?.branding?.color);
        setPhase("ready");
      } catch {
        if (!cancelled) setPhase("notfound");
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (phase === "resolving") {
    return <Shell><Card><div style={{ fontSize: 15, color: MUTED }}>Loading booking page…</div></Card></Shell>;
  }
  if (phase === "notfound") {
    return (
      <Shell>
        <div style={{ background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 24, padding: 28, textAlign: "center" }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: "0 0 8px", color: INK }}>Booking page not found</h1>
          <p style={{ fontSize: 14.5, color: MUTED, margin: 0 }}>This booking link may be incorrect or no longer active.</p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <Card>
        <BookingCard orgId={orgId} seedServices={seedServices} primaryColor={primaryColor} mode="live" />
      </Card>
    </Shell>
  );
}
